import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import {
  CheckCircle2,
  ChevronDown,
  Circle,
  CircleDashed,
  Clock,
  ListChecks,
  MessageSquare,
  SkipForward,
  ArrowLeft,
  Users,
} from "lucide-react";
import { listPortalAgreements, listPortalSessions } from "../../application/portalRecords";
import {
  useAgreementUpdates,
  type AgreementFailure,
  type AgreementStatus,
} from "../../application/useAgreementUpdates";
import type { PortalAgreement, PortalSession } from "../../domain/types";
import { groupAgreementsBySession } from "../../domain/sessionAgreements";
import { formatDay } from "../../domain/dates";

const STATUSES: AgreementStatus[] = ["pending", "in_progress", "completed", "skipped"];

// Skipped is a choice the patient made, not a failure: it reads muted, never
// red. In progress is a half-drawn circle, not a spinner — a spinner says the
// page is still loading.
const STATUS_META: Record<AgreementStatus, { border: string; chip: string; icon: React.ReactNode }> = {
  pending: {
    border: "border-l-gray-200 dark:border-l-gray-700",
    chip: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
    icon: <Circle className="w-3.5 h-3.5" />,
  },
  in_progress: {
    border: "border-l-accent",
    chip: "bg-accent-tint text-accent-text",
    icon: <CircleDashed className="w-3.5 h-3.5" />,
  },
  completed: {
    border: "border-l-green-400",
    chip: "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    icon: <CheckCircle2 className="w-3.5 h-3.5" />,
  },
  skipped: {
    border: "border-l-gray-300 dark:border-l-gray-600",
    chip: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400",
    icon: <SkipForward className="w-3.5 h-3.5" />,
  },
};

const asStatus = (status: string | null): AgreementStatus =>
  STATUSES.includes(status as AgreementStatus) ? (status as AgreementStatus) : "pending";

export default function PortalAgreementsPage() {
  const { t, i18n } = useTranslation();
  const [sessions, setSessions] = useState<PortalSession[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [mobilePanel, setMobilePanel] = useState<"sessions" | "agreements">("sessions");
  const { agreements, setAgreements, changeStatus, saveComment, failure } = useAgreementUpdates();

  const [searchParams] = useSearchParams();

  useEffect(() => {
    Promise.all([listPortalSessions(), listPortalAgreements()])
      .then(([sessionsData, agreementsData]) => {
        setSessions(sessionsData);
        setAgreements(agreementsData);
        const withAgreements = groupAgreementsBySession(sessionsData, agreementsData);
        const preselect = searchParams.get("session");
        const target = preselect ? withAgreements.find((r) => r.session.id === preselect) : null;
        setSelectedSessionId(target?.session.id ?? withAgreements[0]?.session.id ?? null);
        // Arriving from a session row means "show me that session": on a phone,
        // landing on the list instead made the patient pick it a second time.
        if (target) setMobilePanel("agreements");
      })
      .finally(() => setLoading(false));
  }, [searchParams, setAgreements]);

  const data = useMemo(() => groupAgreementsBySession(sessions, agreements), [sessions, agreements]);
  const selectedData = data.find((d) => d.session.id === selectedSessionId) ?? null;

  if (loading) {
    return (
      <div aria-busy="true" className="flex flex-col gap-4">
        <div className="h-8 w-48 animate-pulse rounded-xl bg-gray-200/70 dark:bg-white/5" />
        <div className="h-72 animate-pulse rounded-2xl bg-gray-200/70 dark:bg-white/5" />
      </div>
    );
  }

  return (
    <>
      {/* Page title */}
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-content-primary">
          {t("portal.agreements.title")}
        </h1>
        <p className="text-sm text-content-tertiary mt-1">
          {t("portal.agreements.subtitle")}
        </p>
      </div>

      {data.length === 0 ? (
        <div className="glass-effect glass-border border rounded-2xl p-12 text-center">
          <ListChecks className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-content-tertiary">{t("portal.agreements.empty")}</p>
        </div>
      ) : (
        <div className="glass-effect glass-border border rounded-2xl shadow-sm overflow-hidden flex xl:h-[calc(100vh-16rem)]">
          {/* Left panel — session list */}
          <div className={`${mobilePanel === "agreements" ? "hidden" : "flex"} xl:flex w-full xl:w-64 shrink-0 flex-col border-r border-gray-200 dark:border-white/10`}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 dark:border-white/10 shrink-0">
              <ListChecks className="w-4 h-4 text-brand-500 shrink-0" />
              <span className="text-sm font-semibold text-content-secondary truncate">
                {t("portal.nav.agreements")}
              </span>
            </div>

            <ul className="flex-1 overflow-y-auto divide-y divide-gray-100 dark:divide-white/5">
              {data.map(({ session, agreements: sessionAgreements }) => {
                const completedCount = sessionAgreements.filter((a) => a.status === "completed").length;
                const total = sessionAgreements.length;
                const isSelected = selectedSessionId === session.id;

                return (
                  <li key={session.id}>
                    <button
                      onClick={() => { setSelectedSessionId(session.id); setMobilePanel("agreements"); }}
                      className={`w-full text-left px-4 py-3 transition-colors border-l-2 ${
                        isSelected
                          ? "bg-brand-50 dark:bg-brand-900/20 border-brand-500"
                          : "hover:bg-gray-50 dark:hover:bg-white/5 border-transparent"
                      }`}
                    >
                      <div className="flex items-center gap-1.5 min-w-0">
                        <p className={`text-sm font-medium truncate ${isSelected ? "text-brand-700 dark:text-brand-300" : "text-gray-800 dark:text-gray-200"}`}>
                          {session.title}
                        </p>
                        {session.type === "couple" && <CoupleBadge />}
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-content-tertiary">
                          {formatDay(session.date, i18n.language)}
                        </span>
                        <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                          completedCount === total
                            ? "bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400"
                            : "bg-brand-50 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400"
                        }`}>
                          {completedCount}/{total}
                        </span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Right panel — agreements */}
          <div className={`${mobilePanel === "sessions" ? "hidden" : "flex"} xl:flex flex-1 flex-col overflow-hidden`}>
            {selectedData ? (
              <>
                {/* Session header */}
                <div className="flex items-center gap-2 px-4 sm:px-6 py-3 border-b border-gray-200 dark:border-white/10 shrink-0">
                  <button
                    type="button"
                    onClick={() => setMobilePanel("sessions")}
                    aria-label={t("portal.agreements.backToSessions")}
                    className="xl:hidden flex size-11 -ml-3 items-center justify-center text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 shrink-0"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">
                        {selectedData.session.title}
                      </h2>
                      {selectedData.session.type === "couple" && <CoupleBadge />}
                    </div>
                    <p className="text-xs text-content-tertiary mt-0.5">
                      {formatDay(selectedData.session.date, i18n.language)}
                    </p>
                  </div>
                </div>

                {/* Agreement list */}
                <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
                  <ul className="flex flex-col gap-3">
                    {selectedData.agreements.map((a) => (
                      <AgreementRow
                        key={a.id}
                        agreement={a}
                        failure={failure?.agreementId === a.id ? failure : null}
                        onStatusChange={(status) => changeStatus(a, status)}
                        onCommentSave={(comment) => saveComment(a.id, comment)}
                      />
                    ))}
                  </ul>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center py-16">
                <p className="text-sm text-content-tertiary">{t("portal.agreements.empty")}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function CoupleBadge() {
  const { t } = useTranslation();
  return (
    <span className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-semibold bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400">
      <Users className="w-2.5 h-2.5" />
      {t("session.type.couple", "Pareja")}
    </span>
  );
}

function AgreementRow({
  agreement,
  failure,
  onStatusChange,
  onCommentSave,
}: {
  agreement: PortalAgreement;
  failure: AgreementFailure | null;
  onStatusChange: (status: AgreementStatus) => void;
  onCommentSave: (comment: string) => Promise<boolean>;
}) {
  const { t, i18n } = useTranslation();
  const [commentOpen, setCommentOpen] = useState(!!agreement.patient_comment);
  const [comment, setComment] = useState(agreement.patient_comment ?? "");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const status = asStatus(agreement.status);
  const meta = STATUS_META[status];
  const isCompleted = status === "completed";

  const handleSaveComment = async () => {
    setSaving(true);
    try {
      // A comment that did not reach the server stays dirty, so the button to
      // send it again is still there.
      if (await onCommentSave(comment)) setDirty(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <li className={`border-l-4 ${meta.border} pl-2 pr-4 py-3 rounded-r-xl glass-effect glass-border border border-l-0 flex flex-col gap-2`}>
      {/* Main row */}
      <div className="flex items-start gap-1">
        {/* Complete toggle */}
        <button
          type="button"
          onClick={() => onStatusChange(isCompleted ? "pending" : "completed")}
          aria-label={t(isCompleted ? "portal.agreements.markPending" : "portal.agreements.markComplete")}
          className={`flex size-11 shrink-0 items-center justify-center rounded-full transition-colors ${
            isCompleted
              ? "text-status-success hover:text-content-tertiary"
              : "text-gray-300 hover:text-green-500 dark:text-gray-600 dark:hover:text-green-400"
          }`}
        >
          {isCompleted ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
        </button>

        <p className={`flex-1 pt-2.5 text-sm leading-relaxed ${isCompleted ? "line-through text-content-tertiary" : "text-gray-800 dark:text-gray-100"}`}>
          {agreement.text}
        </p>
      </div>

      {/* Actions row */}
      <div className="flex flex-wrap items-center gap-2 pl-12">
        {/* Every status in plain view, chosen directly: the native picker on a
            phone, a menu on a desktop. */}
        <label className={`relative inline-flex min-h-9 items-center gap-1 rounded-lg pl-2 pr-7 text-xs font-medium ${meta.chip}`}>
          <span aria-hidden="true">{meta.icon}</span>
          <select
            value={status}
            onChange={(e) => onStatusChange(e.target.value as AgreementStatus)}
            aria-label={t("portal.agreements.statusLabel")}
            className="appearance-none bg-transparent py-1.5 pr-1 font-medium focus:outline-none cursor-pointer"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`portal.agreement.status.${s}`)}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 w-3.5 h-3.5" aria-hidden="true" />
        </label>

        {agreement.status_updated_at && (
          <span className="flex items-center gap-1 text-xs text-content-tertiary">
            <Clock className="w-3 h-3" />
            {formatDay(agreement.status_updated_at, i18n.language)}
          </span>
        )}

        <button
          type="button"
          onClick={() => setCommentOpen((v) => !v)}
          aria-expanded={commentOpen}
          className={`ml-auto flex min-h-9 items-center gap-1 rounded-lg px-2 text-xs transition-colors ${
            agreement.patient_comment
              ? "text-brand-600 dark:text-brand-400"
              : "text-content-tertiary hover:text-gray-600 dark:hover:text-gray-300"
          }`}
        >
          <MessageSquare className="w-3.5 h-3.5" aria-hidden="true" />
          {t("portal.agreements.comment")}
        </button>
      </div>

      {failure?.kind === "status" && (
        <p role="alert" className="pl-12 text-xs text-red-600 dark:text-red-400">
          {t("portal.agreements.saveFailed")}
        </p>
      )}

      {/* Comment area */}
      {commentOpen && (
        <div className="pl-12 flex flex-col gap-2">
          {/* 16px on phones: anything smaller makes iOS zoom the page on focus. */}
          <textarea
            value={comment}
            onChange={(e) => { setComment(e.target.value); setDirty(true); }}
            rows={2}
            maxLength={500}
            className="w-full rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 px-3 py-2 text-base sm:text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500 text-gray-800 dark:text-gray-100"
            placeholder={t("portal.agreement.commentPlaceholder")}
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-content-tertiary">{comment.length}/500</span>
            {dirty && (
              <button
                type="button"
                onClick={handleSaveComment}
                disabled={saving}
                className="min-h-9 rounded-lg px-3 text-xs font-medium text-brand-600 hover:bg-brand-50 hover:text-brand-700 dark:text-brand-400 dark:hover:bg-brand-500/10 disabled:opacity-50"
              >
                {saving ? t("common.loading") : t("portal.agreements.saveComment")}
              </button>
            )}
          </div>
          {failure?.kind === "comment" && (
            <p role="alert" className="text-xs text-red-600 dark:text-red-400">
              {t("portal.agreements.commentSaveFailed")}
            </p>
          )}
        </div>
      )}
    </li>
  );
}
